import { useUserSettings } from '@/components/user-settings-context'
import { trpc } from '@/lib/trpc'
import { ContinueOrFreshModal } from './continue-or-fresh-modal'
import { ExpiredSessionModal } from './expired-session-modal'
import { useSession } from './session-context'
import { useTimer } from './timer/timer-context'

interface SessionLifecycleModalsProps {
  /**
   * Programmatically navigate to the Attempts tab/pane after the user
   * dismisses the >24h expired-session notification. Implementation depends
   * on whether we're in the desktop FlexLayout or the mobile tab UI.
   */
  onSwitchToAttempts: () => void
}

/**
 * Renders the two modals that drive lazy session-close UX:
 *   - Continue / Start Fresh (last activity in [2h, 24h))
 *   - Expired session notification (server auto-closed at >= 24h)
 *
 * Modal visibility is driven by `landingMode` per this matrix:
 *   - closed-needs-decision  → continue modal shown
 *   - open-needs-decision    → continue modal shown
 *   - open-expired-stub      → expired modal shown (if expiredNotice !== null)
 *   - all other modes        → both hidden
 */
export function SessionLifecycleModals({
  onSwitchToAttempts,
}: SessionLifecycleModalsProps) {
  const session = useSession()
  const timer = useTimer()
  const { updateSettings } = useUserSettings()
  const markExpiredSeen = trpc.sessions.markExpiredNoticeSeen.useMutation()

  const { landingMode, expiredNotice, needsContinueDecision } = session

  const showContinueModal = needsContinueDecision
  const showExpiredModal =
    landingMode === 'open-expired-stub' && expiredNotice !== null

  const handleContinue = () => {
    // Both modes: just dismiss the modal.
    // - closed-needs-decision: code is already preloaded; on first edit,
    //   saveDraft auto-creates a new session and writes the first sessionDraft
    //   row associated with it.
    // - open-needs-decision: existing session continues; first edit resumes
    //   normal saveDraft flow on it.
    session.acknowledgeContinueDecision()
  }

  const handleStartFresh = async () => {
    if (landingMode === 'closed-needs-decision') {
      // Source session already closed. Reset editor to function stub and timer;
      // the first edit will auto-create a new session.
      session.requestEditorReset()
      timer.resetForNewAttempt()
    } else if (landingMode === 'open-needs-decision') {
      // Existing flow: close current session (snapshots draft if changed),
      // reset the stopwatch synchronously so the user sees 00:00:00 immediately,
      // then open a new session. Reset the editor too — the new session has
      // no draft yet and the load-draft post-setup guard would otherwise leave
      // the editor on the previous attempt's code.
      timer.handleTransitionStartFresh()
      await session.startFreshFromCurrent()
      session.requestEditorReset()
    }
    session.acknowledgeContinueDecision()
  }

  const handleExpiredDismiss = async (suppressFuture: boolean) => {
    updateSettings(
      { editor: { suppressExpirationNotice: suppressFuture } },
      { immediate: true },
    )
    if (expiredNotice?.previousSessionId) {
      await markExpiredSeen.mutateAsync({
        sessionId: expiredNotice.previousSessionId,
      })
    }
    // Auto-expire is NOT the same as Start Fresh: we don't generate a stub
    // session here. The editor falls back to the function stub (since the
    // current session is null), the stopwatch resets to 0, and a new session
    // is only created when the user types or clicks Restore. This matches
    // the previous behavior the user expects for the >=24h flow.
    timer.resetForNewAttempt()
    session.acknowledgeExpiredNotice()
    onSwitchToAttempts()
  }

  return (
    <>
      <ContinueOrFreshModal
        open={showContinueModal}
        onContinue={handleContinue}
        onStartFresh={handleStartFresh}
        isStartingFresh={false}
      />
      <ExpiredSessionModal
        open={showExpiredModal}
        onDismiss={handleExpiredDismiss}
      />
    </>
  )
}
